/* @jsx h */
import { h, type VNode } from "vue";
import { z } from "zod";
import { type FormOptions, type StructLocals } from "@lemmonhq/uikit";
import type {
  CountryOption,
  CurrencyOption,
  TimezoneOption,
} from "../ManagerSettings/subpages/ProfileSettings/types";
import type { SignupForm, SignupStep, Translate } from "./signupTypes";

interface CountryDefaults {
  timezone: string;
  locale: string;
  currency: string;
  timezones?: string[];
  localeMap?: Record<string, string>;
}

const G20_DEFAULTS: Record<string, CountryDefaults> = {
  AR: {
    timezone: "America/Argentina/Buenos_Aires",
    locale: "es_ES",
    currency: "ARS",
  },
  AU: {
    timezone: "Australia/Sydney",
    locale: "en_GB",
    currency: "AUD",
    timezones: [
      "Australia/Sydney",
      "Australia/Melbourne",
      "Australia/Brisbane",
      "Australia/Adelaide",
      "Australia/Darwin",
      "Australia/Perth",
      "Australia/Hobart",
      "Australia/Lord_Howe",
    ],
  },
  BR: {
    timezone: "America/Sao_Paulo",
    locale: "pt_BR",
    currency: "BRL",
    timezones: [
      "America/Sao_Paulo",
      "America/Manaus",
      "America/Belem",
      "America/Fortaleza",
      "America/Recife",
      "America/Bahia",
      "America/Cuiaba",
      "America/Porto_Velho",
      "America/Boa_Vista",
      "America/Noronha",
      "America/Eirunepe",
      "America/Rio_Branco",
    ],
  },
  CA: {
    timezone: "America/Toronto",
    locale: "en_GB",
    currency: "CAD",
    timezones: [
      "America/St_Johns",
      "America/Halifax",
      "America/Moncton",
      "America/Toronto",
      "America/Thunder_Bay",
      "America/Winnipeg",
      "America/Regina",
      "America/Swift_Current",
      "America/Edmonton",
      "America/Vancouver",
      "America/Whitehorse",
      "America/Dawson",
      "America/Yellowknife",
      "America/Inuvik",
      "America/Iqaluit",
      "America/Rankin_Inlet",
      "America/Resolute",
      "America/Cambridge_Bay",
      "America/Creston",
      "America/Dawson_Creek",
      "America/Fort_Nelson",
      "America/Glace_Bay",
      "America/Goose_Bay",
    ],
    localeMap: { fr: "fr_FR", en: "en_GB" },
  },
  CH: {
    timezone: "Europe/Zurich",
    locale: "de_CH",
    currency: "CHF",
    localeMap: { de: "de_CH", fr: "fr_CH", it: "it_CH", en: "en_GB" },
  },
  CN: { timezone: "Asia/Shanghai", locale: "zh_CN", currency: "CNY" },
  DE: { timezone: "Europe/Berlin", locale: "de_DE", currency: "EUR" },
  FR: { timezone: "Europe/Paris", locale: "fr_FR", currency: "EUR" },
  GB: { timezone: "Europe/London", locale: "en_GB", currency: "GBP" },
  GE: { timezone: "Asia/Tbilisi", locale: "en_US", currency: "GEL" },
  ID: {
    timezone: "Asia/Jakarta",
    locale: "en_US",
    currency: "IDR",
    timezones: [
      "Asia/Jakarta",
      "Asia/Pontianak",
      "Asia/Makassar",
      "Asia/Jayapura",
    ],
  },
  IN: { timezone: "Asia/Kolkata", locale: "en_GB", currency: "INR" },
  IT: { timezone: "Europe/Rome", locale: "it_IT", currency: "EUR" },
  JP: { timezone: "Asia/Tokyo", locale: "ja_JP", currency: "JPY" },
  KR: { timezone: "Asia/Seoul", locale: "en_US", currency: "KRW" },
  MX: {
    timezone: "America/Mexico_City",
    locale: "es_ES",
    currency: "MXN",
    timezones: [
      "America/Mexico_City",
      "America/Monterrey",
      "America/Merida",
      "America/Chihuahua",
      "America/Ciudad_Juarez",
      "America/Hermosillo",
      "America/Mazatlan",
      "America/Tijuana",
      "America/Cancun",
      "America/Bahia_Banderas",
      "America/Matamoros",
      "America/Ojinaga",
    ],
  },
  RO: { timezone: "Europe/Bucharest", locale: "en_US", currency: "RON" },
  RU: {
    timezone: "Europe/Moscow",
    locale: "en_US",
    currency: "RUB",
    timezones: [
      "Europe/Kaliningrad",
      "Europe/Moscow",
      "Europe/Samara",
      "Europe/Saratov",
      "Europe/Volgograd",
      "Europe/Ulyanovsk",
      "Asia/Yekaterinburg",
      "Asia/Omsk",
      "Asia/Novosibirsk",
      "Asia/Barnaul",
      "Asia/Tomsk",
      "Asia/Novokuznetsk",
      "Asia/Krasnoyarsk",
      "Asia/Irkutsk",
      "Asia/Chita",
      "Asia/Yakutsk",
      "Asia/Vladivostok",
      "Asia/Magadan",
      "Asia/Sakhalin",
      "Asia/Kamchatka",
      "Asia/Anadyr",
    ],
  },
  SA: { timezone: "Asia/Riyadh", locale: "en_US", currency: "SAR" },
  TR: { timezone: "Europe/Istanbul", locale: "en_US", currency: "TRY" },
  US: {
    timezone: "America/New_York",
    locale: "en_US",
    currency: "USD",
    timezones: [
      "America/New_York",
      "America/Chicago",
      "America/Denver",
      "America/Los_Angeles",
      "America/Anchorage",
      "Pacific/Honolulu",
      "America/Phoenix",
      "America/Detroit",
      "America/Indiana/Indianapolis",
      "America/Indiana/Winamac",
      "America/Indiana/Marengo",
      "America/Indiana/Petersburg",
      "America/Indiana/Vevay",
      "America/Indiana/Vincennes",
      "America/Indiana/Tell_City",
      "America/Kentucky/Louisville",
      "America/Kentucky/Monticello",
      "America/North_Dakota/Center",
      "America/North_Dakota/New_Salem",
      "America/North_Dakota/Beulah",
      "America/Menominee",
      "America/Boise",
    ],
  },
  ZA: { timezone: "Africa/Johannesburg", locale: "en_GB", currency: "ZAR" },
};

const BROWSER_LANG_TO_LOCALE: Record<string, string> = {
  ar: "en_US",
  de: "de_DE",
  en: "en_US",
  es: "es_ES",
  fr: "fr_FR",
  hi: "en_GB",
  it: "it_IT",
  ja: "ja_JP",
  pt: "pt_BR",
  zh: "zh_CN",
};

export function getSmartCountryDefaults(jurisdiction: string): {
  timezone: string;
  locale: string;
  currency?: string;
} {
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const browserLangFull =
    (typeof navigator !== "undefined" ? navigator.language : "en") || "en";
  const browserLang = browserLangFull.split("-")[0].toLowerCase();

  const curated = G20_DEFAULTS[jurisdiction];

  if (!curated) {
    return {
      timezone: browserTz || "UTC",
      locale: BROWSER_LANG_TO_LOCALE[browserLang] || "en_US",
    };
  }

  const timezone =
    curated.timezones && curated.timezones.includes(browserTz)
      ? browserTz
      : curated.timezone;

  const locale =
    curated.localeMap && curated.localeMap[browserLang]
      ? curated.localeMap[browserLang]
      : curated.locale;

  return { timezone, locale, currency: curated.currency };
}

export const createSteps = (t: Translate): SignupStep[] =>
  (
    [
      { id: "workspace", icon: "storefront" },
      { id: "contact", icon: "badge" },
      { id: "account", icon: "lock" },
    ] as const
  ).map(({ id, icon }) => ({
    id,
    icon,
    label: t(`signup.steps.${id}`),
    description: t(`signup.steps.${id}_desc`),
    title: t(`signup.steps.${id}_title`),
    lead: t(`signup.steps.${id}_lead`),
  }));

export const businessTypes = [
  { id: "restaurant", icon: "restaurant" },
  { id: "cafe", icon: "local_cafe" },
  { id: "bar", icon: "local_bar" },
  { id: "bakery", icon: "bakery_dining" },
  { id: "fast_service", icon: "fastfood" },
  { id: "food_truck", icon: "local_shipping" },
  { id: "retail", icon: "storefront" },
  { id: "hotel", icon: "hotel" },
  { id: "other", icon: "more_horiz" },
] as const;

export const createInitialForm = (): SignupForm => ({
  restaurantName: "",
  restaurantType: "restaurant",
  ownerName: "",
  ownerEmail: "",
  phone: "",
  address: "",
  companyRegistration: "",
  jurisdiction: "CH",
  locale: "fr_CH",
  timezone: "Europe/Zurich",
  currency: "CHF",
  password: "",
  confirmPassword: "",
  acceptedTerms: false,
});

export const createStepSchemas = (t: Translate) =>
  [
    z.object({
      restaurantName: z
        .string()
        .trim()
        .min(2, t("signup.errors.restaurant_name")),
      companyRegistration: z.string().trim().optional(),
      jurisdiction: z.string().trim().length(2, t("signup.errors.country")),
      locale: z.string().trim().min(2, t("signup.errors.language")),
      currency: z.string().trim().min(3, t("signup.errors.currency")),
      timezone: z.string().trim().min(2, t("signup.errors.timezone")),
    }),
    z.object({
      ownerName: z.string().trim().min(2, t("signup.errors.owner_name")),
      ownerEmail: z.string().trim().email(t("signup.errors.email")),
      phone: z.string().trim().min(5, t("signup.errors.phone")),
      address: z.string().trim().min(3, t("signup.errors.address")),
    }),
    z
      .object({
        password: z.string().min(6, t("signup.errors.password")),
        confirmPassword: z.string().min(6, t("signup.errors.confirm_password")),
        acceptedTerms: z
          .boolean()
          .refine(Boolean, { message: t("signup.errors.terms") }),
      })
      .refine((value) => value.password === value.confirmPassword, {
        message: t("signup.errors.password_mismatch"),
        path: ["confirmPassword"],
      }),
  ] as const;

const renderFieldGrid = (
  locals: StructLocals,
  keys: string[],
  wideFields: string[] = [],
) => (
  <div class="grid gap-x-4 gap-y-1 sm:grid-cols-2">
    {keys.map((key) => {
      const renderFn = locals.inputs[key];
      return renderFn ? (
        <div
          key={key}
          class={["min-w-0", wideFields.includes(key) && "sm:col-span-2"]}
        >
          {renderFn()}
        </div>
      ) : null;
    })}
  </div>
);

const regionalFields = [
  { key: "jurisdiction", labelKey: "signup.fields.country", icon: "public" },
  { key: "locale", labelKey: "signup.fields.language", icon: "translate" },
  { key: "currency", labelKey: "signup.fields.currency", icon: "payments" },
  { key: "timezone", labelKey: "signup.fields.timezone", icon: "schedule" },
];

const createWorkspaceTemplate =
  (t: Translate, renderBusinessType: () => VNode) => (locals: StructLocals) => (
    <div class="space-y-5">
      {renderFieldGrid(locals, ["restaurantName", "companyRegistration"], [])}

      {renderBusinessType()}

      <section class="rounded-xl border border-base-300 bg-base-200/60">
        <header class="flex items-start gap-3 border-b border-base-300 px-4 py-2.5">
          <span class="material-symbols-outlined mt-0.5 text-xl text-primary">
            language
          </span>
          <div>
            <h3 class="text-sm font-semibold text-accent">
              {t("signup.fields.regional_title")}
            </h3>
            <p class="text-xs text-base-content/60">
              {t("signup.fields.regional_desc")}
            </p>
          </div>
        </header>
        <div class="grid gap-x-4 gap-y-3 p-4 sm:grid-cols-2 xl:grid-cols-4">
          {regionalFields.map(({ key, labelKey, icon }) => {
            const renderFn = locals.inputs[key];
            return renderFn ? (
              <div key={key} class="min-w-0">
                <div class="mb-1.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-base-content/60">
                  <span class="material-symbols-outlined text-base">
                    {icon}
                  </span>
                  <span>{t(labelKey)}</span>
                </div>
                {renderFn()}
              </div>
            ) : null;
          })}
        </div>
      </section>
    </div>
  );

const createGridTemplate =
  (keys: string[], wideFields: string[]) => (locals: StructLocals) =>
    renderFieldGrid(locals, keys, wideFields);

const TERMS_URL = "https://lemmonpos.ch/t-c";
const PRIVACY_URL = "https://lemmonpos.ch/privacy-policy";

// "I agree to Lemmon's {terms} and {privacy}." with both placeholders as links.
// Links inside the checkbox <label> don't toggle it; they open in a new tab so
// the half-filled signup isn't lost.
const renderTermsLabel = (t: Translate) => {
  const links: Record<string, VNode> = {
    terms: (
      <a
        href={TERMS_URL}
        target="_blank"
        rel="noopener noreferrer"
        class="link link-primary"
      >
        {t("signup.fields.terms_link")}
      </a>
    ),
    privacy: (
      <a
        href={PRIVACY_URL}
        target="_blank"
        rel="noopener noreferrer"
        class="link link-primary"
      >
        {t("signup.fields.privacy_link")}
      </a>
    ),
  };
  return (
    <span>
      {t("signup.fields.terms")
        .split(/(\{terms\}|\{privacy\})/)
        .map((part) => links[part.slice(1, -1)] ?? part)}
    </span>
  );
};

type StepOptionsInput = {
  t: Translate;
  /** Business-type tiles, shown between the name fields and regional settings. */
  renderBusinessType: () => VNode;
  countries: CountryOption[];
  loadingCountries: boolean;
  locales: Array<{ value: string; label: string }>;
  timezones: TimezoneOption[];
  loadingTimezones: boolean;
  currencies: CurrencyOption[];
  loadingCurrencies: boolean;
};

export const createStepOptions = ({
  t,
  renderBusinessType,
  countries,
  loadingCountries,
  locales,
  timezones,
  loadingTimezones,
  currencies,
  loadingCurrencies,
}: StepOptionsInput): readonly FormOptions[] => {
  const loading = t("signup.fields.loading");
  return [
    {
      auto: "none",
      template: createWorkspaceTemplate(t, renderBusinessType),
      order: [
        "restaurantName",
        "companyRegistration",
        "jurisdiction",
        "locale",
        "currency",
        "timezone",
      ],
      fields: {
        restaurantName: {
          label: t("signup.fields.restaurant_name"),
          attrs: {
            autocomplete: "organization",
            placeholder: "Cafe Lemmon",
          },
        },
        companyRegistration: {
          label: t("signup.fields.registration"),
          attrs: {
            placeholder: "CHE-123.456.789",
            autocomplete: "off",
          },
        },
        jurisdiction: {
          label: false,
          nullOption: false,
          options: loadingCountries
            ? [{ value: "CH", text: loading }]
            : (countries.length
                ? countries
                : [{ value: "CH", label: "Switzerland", flag: "🇨🇭" }]
              ).map((country) => ({
                value: country.value,
                text: `${country.flag ? `${country.flag} ` : ""}${country.label}`,
              })),
          attrs: {
            disabled: loadingCountries,
          },
        },
        locale: {
          label: false,
          nullOption: false,
          options: (locales.some((locale) => locale.value === "fr_CH")
            ? locales
            : [{ value: "fr_CH", label: "fr_CH" }, ...locales]
          ).map((locale) => ({
            value: locale.value,
            text: locale.label,
          })),
        },
        currency: {
          label: false,
          nullOption: false,
          options: loadingCurrencies
            ? [{ value: "CHF", text: loading }]
            : currencies.map((currency) => ({
                value: currency.value,
                text: `${currency.symbol} - ${currency.label}`,
              })),
          attrs: {
            disabled: loadingCurrencies,
          },
        },
        timezone: {
          label: false,
          nullOption: false,
          options: loadingTimezones
            ? [{ value: "Europe/Zurich", text: loading }]
            : timezones.map((timezone) => ({
                value: timezone.value,
                text: `${timezone.label} (${timezone.offset})`,
              })),
          attrs: {
            disabled: loadingTimezones,
          },
        },
      },
    },
    {
      auto: "none",
      template: createGridTemplate(
        ["ownerName", "ownerEmail", "phone", "address"],
        [],
      ),
      order: ["ownerName", "ownerEmail", "phone", "address"],
      fields: {
        ownerName: {
          label: t("signup.fields.owner_name"),
          attrs: {
            autocomplete: "name",
          },
        },
        ownerEmail: {
          label: t("signup.fields.owner_email"),
          type: "email",
          attrs: {
            autocomplete: "email",
          },
        },
        phone: {
          label: t("signup.fields.phone"),
          type: "tel",
          attrs: {
            autocomplete: "tel",
          },
        },
        address: {
          label: t("signup.fields.address"),
          attrs: {
            autocomplete: "street-address",
          },
        },
      },
    },
    {
      auto: "none",
      template: createGridTemplate(
        ["password", "confirmPassword", "acceptedTerms"],
        ["acceptedTerms"],
      ),
      order: ["password", "confirmPassword", "acceptedTerms"],
      fields: {
        password: {
          label: t("signup.fields.password"),
          type: "password",
          attrs: {
            autocomplete: "new-password",
          },
        },
        confirmPassword: {
          label: t("signup.fields.confirm_password"),
          type: "password",
          attrs: {
            autocomplete: "new-password",
          },
        },
        acceptedTerms: {
          label: () => renderTermsLabel(t),
        },
      },
    },
  ];
};
